import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { ClickableTextPanel } from "@/Common/Components/ClickableText/ClickableTextPanel";
import { AttachDocumentLink } from "@/Common/Components/IconControls/AttachDocumentLink";
import { AttachedDocumentList } from "@/Common/Components/IconControls/AttachedDocumentList";
import { TextAreaAutoSize } from "@/Common/Components/Inputs/TextAreaAutoSize";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { ClinicianSearchStrategy, PersonSearchAutocompleteCustom } from "@/Common/Components/Search/PersonSearchAutocompleteCustom";
import { FileSelectionHelper } from "@/Common/Helpers/FileSelectionHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { FileSelectionContainerType } from "@/Common/Models/FileSelection/FileSelectionContainerType";
import { Person } from "@/Common/Models/Person/Person";
import { MessageOrTaskContext } from "@/Common/Models/UI/Enums";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { CommunicationsTab_OptimizedV2 } from "@/Communications/Components/CommunicationsTab_OptimizedV2";
import { VacationResponderPopupControl } from "@/Communications/Components/VacationResponderPopupControl";
import { FileSelectionContainer } from "@/ExportableFile/Components/FileSelection/FileSelectionContainer";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { ExportableFileModel } from "@/ExportableFile/Models/ExportableFileTableItem";
import { MessageTaskEditContainerState } from "./MessageTaskEditContainer";
import { TaskEditComponent } from "./TaskEditComponent";

import "./CommunicationComponentsCommon.css";
import "./MessageEditComponent.css";

interface IMessageEditComponentProps {
    context: MessageOrTaskContext;
    isTask: boolean;
    model: MessageTaskEditContainerState;
    tempAttachedLabFillerId?: string;
    isAttachingLabToTask: boolean;
    isVacationPopupShown: boolean;
    employeesInVacation: Person[];

    onSubjectChange: (subject: string) => void;
    onBodyChange: (body: string) => void;
    onPriorityChange: (priority: string) => void;
    onStartDateChange: (startDate: Date) => void;
    onDueDateChange: (dueDate: Date) => void;
    onStartTimeChange: (startTime: Date) => void;
    onDueTimeChange: (dueTime: Date) => void;
    onTypeSelectionChange: (typeOfTask: string) => void;

    toggleClickableSubjectVisibility: (then?: () => void) => void;
    toggleClickableBodyVisibility: (then?: () => void) => void;
    onSaveDraftClick: () => void;
    onCancelClick: () => void;
    onSendClick: () => void;
    onVacationPopupCancelClick: () => void;
    onVacationPopupSendClick: () => void;
    onAttachmentClick: () => void;
    openAttachmentPopup: () => void;
    onAttachDocumentClick: (files: ExportableFile[]) => void;
    removeAttachmentFromList: (itemId: number) => void;
    onAttachResultToTaskClick: () => void;

    selectClinician: (personItem: PersonDropdownItem) => void;
    removeClinician: (personItem: PersonDropdownItem) => void;

    selectPatient: (personItem: PersonDropdownItem) => void;
    removePatient: (personItem: PersonDropdownItem) => void;

    isSendLocked?: boolean;
    clickableTextCssClass?: string;
}

class MessageEditComponentState {
    public currentFile: ExportableFileModel;
}

class MessageEditComponent extends BaseReactComponent<IMessageEditComponentProps, MessageEditComponentState> {
    private parentId: string;

    private clinicianSearchStrategy: ClinicianSearchStrategy;

    constructor(props: IMessageEditComponentProps) {
        super(props);

        const initialState = new MessageEditComponentState();
        initialState.currentFile = null;
        this.state = initialState;
    }

    public componentDidMount(): void {
        switch (this.props.context) {
            case MessageOrTaskContext.Documents:
            case MessageOrTaskContext.Labs:
            case MessageOrTaskContext.Unspecified:
            case MessageOrTaskContext.PopulationBasedFunding:
            case MessageOrTaskContext.Encounter:

                let elem = $("#" + "sendTaskMessagePopup");
                let cnt = document.body;
                let dragOptions = {
                    containment: cnt,
                    handle: ".p-header",
                    stop: function() {
                        elem.draggable("destroy");
                        window.setTimeout(() => elem.draggable(dragOptions), 80);
                    }
                };
                elem.draggable(dragOptions);
        }
    }

    public render(): JSX.Element {
        let parentClassname = "workspace";
        let rowClassname = "col-lg-6 col-md-9 col-sm-12 ";
        let bodyId = "posref";
        let bodyClassname = "ws-scrollable";
        let bodyStyle: React.CSSProperties = { maxHeight: "648px", minHeight: "499px" };
        let style = {};
        let headerClassname = "myle-page-header bottom-border";
        let isPopup = false;
        let footerClassName = CommunicationsTab_OptimizedV2.footerCSS;
        this.initData();
        let headerJsx = (
            <div className="row">
                <div className="col-sm-12" style={{ paddingLeft: "30px" }}>
                    <p className="header-font-color">{cacheStore.GetString("CommunicationsLabel")}</p>
                    <h3 className="thin-font">{cacheStore.GetString(this.props.isTask ? "NewTaskLabel" : "NewMessageLabel")}</h3>
                </div>
            </div>
        );

        switch (this.props.context) {
            case MessageOrTaskContext.Documents:
            case MessageOrTaskContext.Labs:
            case MessageOrTaskContext.Unspecified:
            case MessageOrTaskContext.PopulationBasedFunding:
            case MessageOrTaskContext.Encounter:
                this.parentId = "sendTaskMessagePopup";
                parentClassname = "message-edit-component popup task";
                style = { overflow: "visible", transform: "none", left: "22vw", top: "20px" };
                rowClassname = "col-lg-9 col-md-9 col-sm-12 ";
                bodyId = "";
                bodyClassname = "p-body myle-form ";
                bodyStyle = { minHeight: "499px" };
                headerClassname = "p-header";
                isPopup = true;
                footerClassName = "footer-container";
                headerJsx = (
                    <>
                        <h3>{cacheStore.GetString(this.props.isTask ? "NewTaskLabel" : "NewMessageLabel")}</h3>
                        <a className="close" aria-label="Close" onClick={() => this.props.onCancelClick()}>×</a>
                    </>
                );
                break;
        }

        let areSubjectAndBodyEmpty = this.areSubjectAndBodyEmpty();
        let subjectClassName = areSubjectAndBodyEmpty ?
            rowClassname + "form-field-kendo-component has-error" :
            rowClassname + "form-field-kendo-component";

        let txtBodyClassname = areSubjectAndBodyEmpty ?
            rowClassname + "form-field-kendo-component has-error" :
            rowClassname + "form-field-kendo-component";
        parentClassname = `message-edit-component ${parentClassname}`;
        const clickableTextCssClass = this.props.clickableTextCssClass
            ? `clickable-text-container ${this.props.clickableTextCssClass}`
            : "clickable-text-container";

        return (
            <div id={this.parentId} className={parentClassname} style={style} onClick={() => this.containerClick()}>
                {this.state.currentFile &&
                    this.renderAttachmentPreview(this.state.currentFile)
                }
                <div className={headerClassname}>
                    {headerJsx}
                </div>
                <div id={bodyId} className={bodyClassname} style={bodyStyle}>
                    <div className="myle-doc-tb main-content myle-form documents">
                        <div className="row">
                            <div className="col-lg-2 col-md-3 col-sm-12 vcenter label-text-alignment">
                                <label className="form-label">{cacheStore.GetString("ToM2Label")}</label>
                            </div>
                            <div className={rowClassname + "form-field-kendo-component"}>
                                <PersonSearchAutocompleteCustom
                                    focusOnLoad={true}
                                    disabled={false}
                                    valid={!isArrayNullOrUndefinedOrEmpty(this.props.model.selectedClinicians)}
                                    componentId="add_task_clinicians"
                                    dataCy="add-task-clinicians"
                                    searchStrategy={this.clinicianSearchStrategy}
                                    selectedItemList={this.props.model.selectedClinicians.toImmutable()}
                                    onSelectionCallback={(personItem: PersonDropdownItem) => this.props.selectClinician(personItem)}
                                    onClearCallback={(personItem: PersonDropdownItem) => this.props.removeClinician(personItem)}
                                    clearOp={LocalOperations.Documents.Edit_Clinician_remove}
                                    closeDropDownAfterSelection={true}
                                    allowMultipleItemSelection={true}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-lg-2 col-md-3 col-sm-12 vcenter label-text-alignment">
                                <label className="form-label">{cacheStore.GetString("AttachPatientLabel")}</label>
                            </div>
                            <div className={rowClassname + "form-field-kendo-component"}>
                                <PersonSearchAutocompleteCustom
                                    focusOnLoad={false}
                                    componentId="_pautocomplete"
                                    dataCy="add-task-patient"
                                    selectedItemList={this.props.model.selectedPatients.toImmutable()}
                                    disabled={false}
                                    useBlurEvent={true}
                                    closeDropDownAfterSelection={true}
                                    onSelectionCallback={(personItem: PersonDropdownItem) => this.props.selectPatient(personItem)}
                                    onClearCallback={(personItem: PersonDropdownItem) => this.props.removePatient(personItem)}
                                />
                            </div>
                        </div>
                        <div className="row" style={{ marginBottom: "2px" }}>
                            <div className="col-lg-2 col-md-3 col-sm-12 vcenter label-text-alignment">
                                <label className="form-label">{cacheStore.GetString("SubjectLabel")}</label>
                            </div>
                            <div className={subjectClassName}>
                                <TextboxControl
                                    debounceMs={200}
                                    onChange={(value: string) => {
                                        this.props.onSubjectChange(value);
                                    }}
                                    className="form-control"
                                    value={this.props.model.subject}
                                    valid={!areSubjectAndBodyEmpty}
                                    id="subject"
                                    dataCy="add-task-subject"
                                />

                                {this.props.model.isSubjectClickableTextShown && (
                                    <div id="subjectClickableText" className={clickableTextCssClass}>
                                        <ClickableTextPanel
                                            cssClass="text-helper-container"
                                            context={this.props.isTask ? "STASKS" : "SMESSAGES"}
                                            inputElementId="subject"
                                            onCloseEvent={() => this.props.toggleClickableSubjectVisibility()}
                                            reduceHeight={isPopup}
                                            dataCy="messageEdit-subject"
                                        />
                                    </div>
                                )}
                                <div className="text-right">
                                    <a
                                        onClick={e => this.subjectSelectfromlistClick(e)}
                                        data-cy="messageEdit-selectSubjectFromList"
                                    >
                                        {cacheStore.GetString("SelectFromListLabel")}
                                    </a>
                                </div>
                            </div>
                        </div>
                        {this.props.isTask && (
                            <TaskEditComponent
                                startDate={new Date(this.props.model.taskStartDate?.toString())}
                                dueDate={new Date(this.props.model.taskDueDate?.toString())}
                                selectedGroupTypeId={this.props.model.selectedTaskGroupTypeId}
                                onTypeSelectionChange={val => this.props.onTypeSelectionChange(val)}
                                onStartDateChange={date => this.props.onStartDateChange(date)}
                                onDueDateChange={date => this.props.onDueDateChange(date)}
                                onStartTimeChange={time => this.props.onStartTimeChange(time)}
                                onDueTimeChange={time => this.props.onDueTimeChange(time)}
                            />
                        )}
                        <div className="row">
                            <div className="col-lg-2 col-md-3 col-sm-12 vcenter label-text-alignment">
                                <label className="form-label">{cacheStore.GetString("PriorityLabel")}</label>
                            </div>
                            <div className={rowClassname + "form-field-kendo-component"}>
                                <select onChange={(e: any) => this.props.onPriorityChange(e.target.value)} className="form-control myle-control" data-cy="add-task-priority" value={this.props.model.selectedPriorityId}>
                                    {this.props.model.priorities.map((p, idx) => (
                                        <option key={p.LookupID + idx} value={p.LookupID}> {cacheStore.GetLocalizedValueByLookupId(p.LookupID)}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="row" style={{ marginBottom: "2px" }}>

                            <div className="col-lg-2 col-md-3 col-sm-12 label-text-alignment" style={{ marginTop: "15px" }}>
                                <label className="form-label">{cacheStore.GetString("BodyLabel")}</label>
                            </div>
                            <div className={txtBodyClassname}>
                                {isPopup // This will keep the height of the text area to 3 rows and not allow any auto resize of the text area.
                                    ? (
                                        <textarea
                                            className="form-control"
                                            style={{ resize: "none" }}
                                            id="body"
                                            data-cy="add-task-body"
                                            ref="body"
                                            value={this.props.model.body}
                                            onChange={(value: any) => { this.props.onBodyChange(value.target.value); }}
                                            rows={3}
                                        />
                                    )
                                    : (
                                        <TextAreaAutoSize
                                            id="body"
                                            ref="body"
                                            value={this.props.model.body}
                                            onTextChangeCallback={(value: string) => { this.props.onBodyChange(value); }}
                                            maxHeight="205px"
                                            dataCy="messageEdit-body"
                                        />
                                    )
                                }
                                {this.props.model.isBodyClickableTextShown && this.props.isTask && isPopup && (
                                    <div id="bodyClickableText" className="clickable-text-container">
                                        <ClickableTextPanel
                                            cssClass="text-helper-container bottom-zero"
                                            context={this.props.isTask ? "BTASKS" : "BMESSAGES"}
                                            inputElementId="body"
                                            onCloseEvent={() => this.props.toggleClickableBodyVisibility()}
                                            reduceHeight={isPopup}
                                        />
                                    </div>
                                )}
                                {this.props.model.isBodyClickableTextShown && (!this.props.isTask || !isPopup) && (
                                    <div id="bodyClickableText" className={clickableTextCssClass}>
                                        <ClickableTextPanel
                                            cssClass="text-helper-container"
                                            context={this.props.isTask ? "BTASKS" : "BMESSAGES"}
                                            inputElementId="body"
                                            onCloseEvent={() => this.props.toggleClickableBodyVisibility()}
                                            reduceHeight={isPopup}
                                        />
                                    </div>
                                )}
                                <div className="text-right">
                                    <a
                                        onClick={e => this.bodySelectfromlistClick(e)}
                                    >
                                        {cacheStore.GetString("SelectFromListLabel")}
                                    </a>
                                </div>
                            </div>
                        </div>
                        {!IsNullOrWhiteSpace(this.props.tempAttachedLabFillerId) && (
                            <div className="row">
                                <div className="col-lg-2 col-md-3 col-sm-12 vcenter label-text-alignment" />
                                <div className={rowClassname + "form-field-kendo-component"}>
                                    <input
                                        name="labs_check"
                                        id="labs_check"
                                        className="pull-left"
                                        type="checkbox"
                                        value="static"
                                        checked={this.props.isAttachingLabToTask}
                                        onClick={() => this.props.onAttachResultToTaskClick()}
                                        style={{ marginRight: "5px" }}
                                    />
                                    <span>{cacheStore.GetString("AttachResultToTaskCheckboxLabel")}</span>
                                </div>
                            </div>
                        )}
                        {this.props.model.selectedPatients.length > 0 && (
                            <div className="flex-container" style={{ alignItems: "baseline", justifyContent: "flex-start" }}>
                                <div className="col-lg-2 col-md-3 col-sm-12 vcenter label-text-alignment">
                                    <label className="form-label">{cacheStore.GetString("AttachmentsLabel")}</label>
                                </div>
                                <div className={rowClassname + "form-field-kendo-component"}>
                                    <AttachDocumentLink onClick={() => this.props.openAttachmentPopup()} />
                                    <div className={isPopup ? "popup-attachment-task-attachment" : ""}>
                                        {this.props.model.selectedAttachments.length > 0 && (
                                            <AttachedDocumentList
                                                selectedAttachments={this.props.model.selectedAttachments}
                                                removeAttachmentFromList={p => this.props.removeAttachmentFromList(p)}
                                                resolveDescription={p => this.resolveDescription(p)}
                                                dataCy="messageEditComponent-attachedDocument"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className={footerClassName}>
                    <div className="col-lg-12 col-md-12 col-sm-12 text-right">
                        <div className="flex-container">
                            <ButtonControl
                                className="btn btn-primary push-right-5"
                                dataCy="add-task-save"
                                label={cacheStore.GetString("SaveDraftLabel")}
                                onClick={() => this.props.onSaveDraftClick()}
                                disabled={this.props.model.selectedClinicians.length <= 0 || areSubjectAndBodyEmpty}
                            />
                            <div className="text-right">
                                <ButtonControl
                                    className="btn btn-default push-right-5"
                                    dataCy="add-task-cancel"
                                    label={cacheStore.GetString("CancelLabel")}
                                    onClick={() => this.props.onCancelClick()}
                                />
                                <ButtonControl
                                    className="btn btn-primary"
                                    dataCy="add-task-send"
                                    label={cacheStore.GetString("SendLabel")}
                                    onClick={() => this.props.onSendClick()}
                                    disabled={this.props.model.selectedClinicians.length <= 0 || areSubjectAndBodyEmpty || this.props.isSendLocked}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {this.props.isVacationPopupShown && (
                    <VacationResponderPopupControl
                        containerId="vacation_container"
                        EmployeesInVacation={this.props.employeesInVacation}
                        cancelClickCallback={() => this.props.onVacationPopupCancelClick()}
                        sendClickCallback={() => this.props.onVacationPopupSendClick()}
                        isSendLocked={this.props.isSendLocked}
                    />
                )}
                {this.props.model.isAttachmentsPopupOpen && (
                    <FileSelectionContainer
                        patient={this.props.model.selectedPatients[0]}
                        title={cacheStore.GetString("AttachFilesLabel")}
                        type={FileSelectionContainerType.Attachment}
                        onClose={() => this.props.openAttachmentPopup()}
                        onPrintOrAttachClicked={selectedFiles => this.props.onAttachDocumentClick(selectedFiles)}
                        popupStyle={{ position: "fixed" }}
                        autoManageOverlay={this.props.context === MessageOrTaskContext.Communications}
                    />
                )}
            </div>
        );
    }

    private bodySelectfromlistClick(e: React.MouseEvent): void {
        e.stopPropagation();
        this.props.toggleClickableBodyVisibility(
            () => {
                if (this.props.model.isBodyClickableTextShown)
                    $("#body")[0].scrollIntoView();
            }
        );
    }

    private subjectSelectfromlistClick(e: React.MouseEvent): void {
        e.stopPropagation();
        this.props.toggleClickableSubjectVisibility(
            () => {
                if (this.props.model.isSubjectClickableTextShown)
                    $("#subject")[0].scrollIntoView();
            }
        );
    }

    public areSubjectAndBodyEmpty(): boolean {
        return IsNullOrWhiteSpace(this.props.model.subject) && IsNullOrWhiteSpace(this.props.model.body);
    }

    public containerClick(): void {
        this.props.model.isSubjectClickableTextShown && this.props.toggleClickableSubjectVisibility();
        this.props.model.isBodyClickableTextShown && this.props.toggleClickableBodyVisibility();
    }

    private initData(): void {
        this.clinicianSearchStrategy = new ClinicianSearchStrategy(cacheStore.CliniciansAndEmployeeGroups);
    }

    private resolveDescription(attachment: ExportableFileModel): string {
        if (!IsNullOrWhiteSpace(attachment.displayDescription))
            return attachment.displayDescription;

        return FileSelectionHelper.determineTypeName(attachment.Model.fileType);
    }

    private renderAttachmentPreview(attachedObj: ExportableFileModel): JSX.Element {
        if (!attachedObj)
            return null;

        attachedObj.Model.getFetchURL(fetchUrl => {
            MyleHelper.OpenNewWindow(fetchUrl);
            this.mutateState(s => s.currentFile = null);
        });
    }
}

export { MessageEditComponent };
